import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tag, Gift, Plus, X, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable widget that lets a user apply/remove a discount code AND a gift
 * card code on an existing booking. Used in 3 places:
 *   1. CRM Booking Detail "Booking Summary" right rail (mode='admin')
 *   2. EditBookingDialog detailed view  (mode='admin')
 *   3. Customer portal booking detail   (mode='customer')
 *
 * In admin mode, applying/removing requires a 4-digit PIN (employee or admin).
 * In customer mode, the customer can only APPLY (no remove — they contact
 * support if they made a mistake).
 *
 * Props:
 *   - booking: the booking dict (must include booking_id, discount?, gift_card?, customer_email)
 *   - mode: 'admin' | 'customer'
 *   - compact: when true, renders a condensed inline UI (default false)
 *   - onUpdated: (updatedBooking) => void — fired after every successful
 *     apply/remove so the parent can refresh derived state
 */
export const BookingDiscountsWidget = ({ booking, mode = 'admin', compact = false, onUpdated }) => {
  const [busy, setBusy] = useState(null); // 'apply-discount' | 'remove-discount' | 'apply-gift' | 'remove-gift'

  const baseUrl = mode === 'admin'
    ? `${API_URL}/api/admin/bookings/${booking.booking_id}`
    : `${API_URL}/api/customer/bookings/${booking.booking_id}`;

  const callApi = async (path, payload) => {
    setBusy(path);
    try {
      const res = await axios.post(`${baseUrl}/${path}`, payload, { withCredentials: true });
      toast.success(res.data?.message || 'Updated');
      onUpdated?.(res.data?.booking);
      return res.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
      throw e;
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="booking-discounts-widget">
      <CodeRow
        kind="discount"
        icon={Tag}
        color="#06D6A0"
        applied={booking.discount}
        compact={compact}
        mode={mode}
        busy={busy}
        onApply={(code, pin) => callApi('apply-discount', { code, pin })}
        onRemove={(pin) => callApi('remove-discount', { pin })}
      />
      <CodeRow
        kind="gift-card"
        icon={Gift}
        color="#FB923C"
        applied={booking.gift_card && !booking.gift_card.refunded_at ? booking.gift_card : null}
        compact={compact}
        mode={mode}
        busy={busy}
        onApply={(code, pin) => callApi('apply-gift-card', { code, pin })}
        onRemove={(pin) => callApi('remove-gift-card', { pin })}
      />
    </div>
  );
};

const CodeRow = ({ kind, icon: Icon, color, applied, compact, mode, busy, onApply, onRemove }) => {
  const [code, setCode] = useState('');
  const [pin, setPin] = useState('');
  const [open, setOpen] = useState(false);
  const [removePin, setRemovePin] = useState('');
  const [removing, setRemoving] = useState(false);

  const label = kind === 'discount' ? 'Discount code' : 'Gift card';
  const placeholder = kind === 'discount' ? 'PROMO10' : 'GIFT-XXXXXX';
  const testKey = kind === 'discount' ? 'discount' : 'gift-card';
  const isBusyApply = busy === (kind === 'discount' ? 'apply-discount' : 'apply-gift-card');
  const isBusyRemove = busy === (kind === 'discount' ? 'remove-discount' : 'remove-gift-card');

  const submitApply = async () => {
    if (!code.trim()) { toast.error(`Enter a ${label.toLowerCase()}`); return; }
    if (mode === 'admin' && (!pin.trim() || pin.trim().length !== 4)) {
      toast.error('Enter a 4-digit PIN');
      return;
    }
    try {
      await onApply(code.trim().toUpperCase(), pin.trim());
      setCode(''); setPin(''); setOpen(false);
    } catch (_) { /* toasted */ }
  };

  const submitRemove = async () => {
    if (mode === 'admin' && (!removePin.trim() || removePin.trim().length !== 4)) {
      toast.error('Enter a 4-digit PIN to remove');
      return;
    }
    try {
      await onRemove(removePin.trim());
      setRemovePin(''); setRemoving(false);
    } catch (_) { /* toasted */ }
  };

  if (applied) {
    const amount = kind === 'discount' ? Number(applied.amount || 0) : Number(applied.amount_applied || 0);
    return (
      <div
        className="rounded-xl p-3 flex items-start justify-between gap-3"
        style={{ background: `${color}15`, border: `1px solid ${color}33` }}
        data-testid={`${testKey}-applied-row`}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <Icon className="w-3.5 h-3.5" style={{ color }} />
            <span className="text-xs font-semibold uppercase tracking-widest" style={{ color }}>{label} applied</span>
          </div>
          <p className="font-mono font-bold text-[#1E1E24] text-sm mt-0.5" data-testid={`${testKey}-applied-code`}>{applied.code}</p>
          <p className="text-xs text-[#6B6B7A]">−${amount.toFixed(2)} off</p>
        </div>
        {mode === 'admin' ? (
          removing ? (
            <div className="space-y-1.5">
              <Input
                type="password"
                inputMode="numeric"
                maxLength={4}
                placeholder="PIN"
                value={removePin}
                onChange={(e) => setRemovePin(e.target.value.replace(/\D/g, ''))}
                className="w-24 h-8 text-xs font-mono"
                data-testid={`${testKey}-remove-pin`}
              />
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => { setRemoving(false); setRemovePin(''); }} className="h-7 px-2 text-[10px]" data-testid={`${testKey}-remove-cancel`}>
                  Cancel
                </Button>
                <Button size="sm" onClick={submitRemove} disabled={isBusyRemove} className="h-7 px-2 text-[10px] bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid={`${testKey}-remove-confirm`}>
                  {isBusyRemove ? '…' : 'Remove'}
                </Button>
              </div>
            </div>
          ) : (
            <Button
              size="sm" variant="ghost"
              onClick={() => setRemoving(true)}
              className="text-[#FF3B6B] hover:bg-[#FF3B6B]/10 h-7 px-2 text-[10px]"
              data-testid={`${testKey}-remove-btn`}
            >
              <X className="w-3 h-3 mr-0.5" /> Remove
            </Button>
          )
        ) : (
          <span className="text-[10px] text-[#6B6B7A] italic">Contact support to remove</span>
        )}
      </div>
    );
  }

  if (compact && !open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed border-[#E8E5DF] hover:border-[#FF3B6B] hover:bg-[#FFFDF8] text-xs text-[#6B6B7A] transition-all"
        data-testid={`${testKey}-add-btn`}
      >
        <Plus className="w-3.5 h-3.5" />
        <span>Apply {label.toLowerCase()}</span>
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-[#E8E5DF] bg-[#FFFDF8] p-3" data-testid={`${testKey}-apply-form`}>
      <div className="flex items-center gap-1.5 mb-2">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <Label className="text-xs font-semibold text-[#1E1E24]">{label}</Label>
      </div>
      <div className="space-y-2">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 32))}
          placeholder={placeholder}
          className="h-8 text-xs font-mono"
          data-testid={`${testKey}-code-input`}
          onKeyDown={(e) => { if (e.key === 'Enter') submitApply(); }}
        />
        {mode === 'admin' && (
          <Input
            type="password"
            inputMode="numeric"
            maxLength={4}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            placeholder="4-digit PIN"
            className="h-8 text-xs font-mono tracking-widest"
            data-testid={`${testKey}-pin-input`}
          />
        )}
        <div className="flex gap-1.5">
          {compact && (
            <Button size="sm" variant="outline" onClick={() => { setOpen(false); setCode(''); setPin(''); }} className="h-7 px-2 text-[10px]" data-testid={`${testKey}-cancel-btn`}>
              Cancel
            </Button>
          )}
          <Button
            size="sm" onClick={submitApply} disabled={isBusyApply}
            className="h-7 px-2 text-[10px] text-white flex-1"
            style={{ background: color }}
            data-testid={`${testKey}-apply-btn`}
          >
            {isBusyApply ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Sparkles className="w-3 h-3 mr-0.5" /> Apply</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Unified booking-totals summary block. Reads `subtotal`, `discount`,
 * `special_offer`, `loyalty_voucher`, `gift_card`, `tax`, `total`,
 * `balance_due`, `credit_balance` straight off the booking dict — same
 * shape across CRM, invoice, and customer portal.
 */
export const BookingTotalsSummary = ({ booking, compact = false }) => {
  const baseSubtotal = computePreDiscountSubtotal(booking);
  const discount = booking.discount;
  const offer = booking.special_offer;
  const freebie = booking.loyalty_voucher;
  const giftCard = booking.gift_card && !booking.gift_card.refunded_at ? booking.gift_card : null;
  const subtotal = Number(booking.subtotal || 0);
  const tax = Number(booking.tax || 0);
  const total = Number(booking.total || 0);
  const credit = Number(booking.credit_balance || 0);
  const balanceDue = Number(booking.balance_due ?? total);

  const rowCls = compact ? 'text-xs' : 'text-sm';

  return (
    <div className="space-y-1.5" data-testid="booking-totals-summary">
      <Row label="Subtotal" value={`$${baseSubtotal.toFixed(2)}`} cls={rowCls} testid="totals-subtotal" />
      {discount?.amount > 0 && (
        <Row label={`Discount (${discount.code})`} value={`−$${Number(discount.amount).toFixed(2)}`} cls={`${rowCls} text-[#06D6A0]`} testid="totals-discount" />
      )}
      {offer?.amount > 0 && (
        <Row label={`★ ${offer.title || 'Special offer'}`} value={`−$${Number(offer.amount).toFixed(2)}`} cls={`${rowCls} text-[#FF3B6B]`} testid="totals-offer" />
      )}
      {freebie?.amount > 0 && (
        <Row label={`Loyalty freebie`} value={`−$${Number(freebie.amount).toFixed(2)}`} cls={`${rowCls} text-[#06D6A0]`} testid="totals-freebie" />
      )}
      {giftCard?.amount_applied > 0 && (
        <Row label={`Gift card (${giftCard.code})`} value={`−$${Number(giftCard.amount_applied).toFixed(2)}`} cls={`${rowCls} text-[#FB923C]`} testid="totals-gift-card" />
      )}
      <Row label="Tax (13%)" value={`$${tax.toFixed(2)}`} cls={rowCls} testid="totals-tax" />
      <div className="border-t border-[#E8E5DF] pt-2 flex justify-between text-base font-semibold">
        <span className="text-[#1E1E24]">Total</span>
        <span className="text-[#FF3B6B]" data-testid="totals-total">${total.toFixed(2)}</span>
      </div>
      {balanceDue !== total && (
        <Row
          label={credit > 0 ? 'Credit balance' : 'Balance due'}
          value={credit > 0 ? `+$${credit.toFixed(2)}` : `$${balanceDue.toFixed(2)}`}
          cls={`text-xs font-semibold ${credit > 0 ? 'text-[#06D6A0]' : balanceDue > 0 ? 'text-[#FB923C]' : 'text-[#6B6B7A]'}`}
          testid="totals-balance"
        />
      )}
    </div>
  );
};

const Row = ({ label, value, cls, testid }) => (
  <div className={`flex justify-between ${cls}`} data-testid={testid}>
    <span className="text-[#6B6B7A]">{label}</span>
    <span className="text-[#1E1E24] font-medium">{value}</span>
  </div>
);

const computePreDiscountSubtotal = (b) => {
  const pkg = Number(b.package_snapshot?.price || 0);
  const addons = Number(b.add_ons_total || 0);
  const custom = Number(b.custom_charges_total || 0);
  return pkg + addons + custom;
};

export default BookingDiscountsWidget;
